import React, { useState, useEffect, useRef } from 'react';
import { Outlet, useLocation, Link, useNavigate } from 'react-router-dom';
import SideNavBar from './SideNavBar.jsx';
import AdminDashboard from './AdminDashboard.jsx';
import OnboardingTour from './OnboardingTour.jsx';
import ModerationPanel from './ModerationPanel.jsx';
import ErrorBoundary from './ErrorBoundary.jsx';
import OfflineIndicator from './OfflineIndicator.jsx';
import { 
  PlusIcon, BellIcon, CheckCircleIcon, FireIcon, ChatBubbleLeftIcon, AtSymbolIcon,
  RocketLaunchIcon, StarIcon, ChatBubbleBottomCenterTextIcon 
} from '@heroicons/react/24/solid';
import {
  EyeIcon, EyeSlashIcon, BoltIcon, Bars3Icon, XMarkIcon, TrashIcon, CheckIcon,
  GiftIcon, ShieldCheckIcon, ExclamationTriangleIcon, HandThumbUpIcon,
  UserPlusIcon, MegaphoneIcon, PlayIcon, ServerIcon, TrophyIcon
} from '@heroicons/react/24/outline';
import { useAuth, useData } from '../hooks'; 
import { collection, query, orderBy, limit, onSnapshot, doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { 
    markNotificationRead, 
    markAllNotificationsRead, 
    saveFcmToken,
    deleteNotification,
    deleteAllNotifications,
    submitFeedback 
} from '../api';
import { formatDistanceToNow } from 'date-fns';
import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications'; // FIXED IMPORT

// --- CONFIGURATION: APP VERSION ---
const APP_VERSION = '1.0.1'; 

// --- HELPER: FRIENDLY ERROR MAPPING ---
const getFriendlyErrorMessage = (msg) => {
    if (!msg) return "";
    const lowerMsg = msg.toLowerCase();
    
    // Custom App Errors
    if (lowerMsg.includes("handle is already taken")) return "That handle is already occupied by another debater. Please choose a unique username.";
    if (lowerMsg.includes("username is required")) return "Please enter a username to continue.";
    if (lowerMsg.includes("username cannot contain spaces")) return "Usernames must be one word (no spaces).";
    
    // Firebase Auth Errors
    if (lowerMsg.includes("email-already-in-use")) return "This email address is already registered. Try signing in instead.";
    if (lowerMsg.includes("user-not-found") || lowerMsg.includes("wrong-password") || lowerMsg.includes("invalid-credential")) return "Incorrect email or password. Please try again.";
    if (lowerMsg.includes("invalid-email")) return "Please enter a valid email address.";
    if (lowerMsg.includes("weak-password")) return "Password is too weak. It must be at least 6 characters.";
    if (lowerMsg.includes("network-request-failed")) return "Connection failed. Please check your internet settings.";
    if (lowerMsg.includes("too-many-requests")) return "Too many attempts. Access temporarily blocked for safety. Try again later.";
    
    // Fallback
    return msg.replace('Firebase: ', '').replace('Error: ', '');
};

// --- NEW: LEGAL ACCEPTANCE MODAL ---
const LegalModal = ({ isOpen, onClose, onConfirm, isSubmitting }) => {
    const [agreed, setAgreed] = useState(false);
    
    // --- DYNAMIC LINK LOGIC ---
    const isNative = Capacitor.isNativePlatform();
    const isIOS = Capacitor.getPlatform() === 'ios';
    const baseUrl = isNative ? "https://verbi-bd770.web.app" : window.location.origin;

    const termsLink = isIOS 
        ? "https://www.apple.com/legal/internet-services/itunes/dev/stdeula/" 
        : `${baseUrl}/terms.html`;
    
    const privacyLink = `${baseUrl}/privacy_policy.html`;

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-8 bg-slate-900/95 backdrop-blur-sm animate-fade-in">
            <div className="bg-white w-full max-w-sm md:max-w-md rounded-3xl shadow-2xl overflow-hidden animate-slide-up relative border-4 border-slate-900">
                <button
                    onClick={onClose}
                    disabled={isSubmitting}
                    className="absolute top-4 right-4 p-2.5 md:p-3 text-slate-400 hover:text-slate-900 transition-colors z-10"
                >
                    <XMarkIcon className="w-5 h-5 md:w-6 md:h-6" />
                </button>

                <div className="p-6 md:p-8 text-center">
                    <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4 border-4 border-white shadow-lg">
                        <ShieldCheckIcon className="w-8 h-8 text-slate-900" />
                    </div>
                    <h2 className="text-2xl font-black text-slate-900 uppercase tracking-tight mb-2">Final Step</h2>
                    <p className="text-slate-500 font-medium text-sm mb-6">
                        Please accept the rules of engagement to enter the arena.
                    </p>

                    <div className="bg-slate-50 rounded-xl p-4 mb-6 border border-slate-200 text-left">
                        <label className="flex items-start cursor-pointer group">
                            <div className="relative flex items-center h-5">
                                <input 
                                    type="checkbox" 
                                    checked={agreed} 
                                    onChange={(e) => setAgreed(e.target.checked)} 
                                    className="peer h-5 w-5 rounded border-2 border-slate-300 text-slate-900 focus:ring-0 cursor-pointer transition-all checked:bg-slate-900 checked:border-slate-900"
                                />
                            </div>
                            <div className="ml-3 text-xs text-slate-600 font-medium leading-relaxed">
                                I agree to the{' '}
                                <a href={termsLink} target="_blank" rel="noopener noreferrer" className="text-blue-600 font-bold underline decoration-blue-300 underline-offset-2 hover:text-blue-800" onClick={(e) => e.stopPropagation()}>
                                    {isIOS ? 'Apple EULA' : 'Terms of Service'}
                                </a>
                                {' '}and{' '}
                                <a href={privacyLink} target="_blank" rel="noopener noreferrer" className="text-blue-600 font-bold underline decoration-blue-300 underline-offset-2 hover:text-blue-800" onClick={(e) => e.stopPropagation()}>
                                    Privacy Policy
                                </a>.
                                <p className="mt-1 text-[10px] text-slate-400 uppercase font-bold tracking-wider">
                                    Zero tolerance for objectionable content.
                                </p>
                            </div>
                        </label>
                    </div>

                    <button 
                        onClick={onConfirm}
                        disabled={!agreed || isSubmitting}
                        className={`w-full py-4 rounded-xl uppercase tracking-widest font-black transition-all shadow-lg active:scale-95 flex items-center justify-center ${
                            agreed 
                                ? 'bg-green-500 text-white hover:bg-green-600 shadow-green-200' 
                                : 'bg-slate-200 text-slate-400 cursor-not-allowed'
                        }`}
                    >
                        {isSubmitting ? (
                            <>
                                <BoltIcon className="w-5 h-5 animate-spin mr-2" />
                                Processing...
                            </>
                        ) : (
                            <>
                                <CheckIcon className="w-5 h-5 mr-2" />
                                Proceed to Debate
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

// --- NEW: REVIEW MODAL (5-MINUTE TRIGGER) ---
const ReviewModal = ({ onClose }) => {
    const [rating, setRating] = useState(0);
    const [feedback, setFeedback] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleSubmit = async () => {
        if (rating === 0) return alert("Please select a star rating.");
        setIsSubmitting(true);
        try {
            await submitFeedback(rating, feedback);
            localStorage.setItem('verbi_review_completed', 'true');
            alert("Thank you! Your feedback helps us build a better arena.");
            onClose();
        } catch (e) {
            alert("Error submitting feedback: " + e.message);
        }
        setIsSubmitting(false);
    };

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-8 bg-slate-900/90 backdrop-blur-sm animate-fade-in">
            <div className="bg-white w-full max-w-sm md:max-w-md rounded-3xl shadow-2xl overflow-hidden animate-slide-up relative">
                <button onClick={onClose} className="absolute top-4 right-4 p-2.5 md:p-3 text-slate-400 hover:text-slate-900 transition-colors">
                    <XMarkIcon className="w-5 h-5 md:w-6 md:h-6" />
                </button>

                <div className="p-6 md:p-8 text-center">
                    <div className="w-16 h-16 bg-yellow-400 rounded-full flex items-center justify-center mx-auto mb-4 border-4 border-white shadow-lg">
                        <ChatBubbleBottomCenterTextIcon className="w-8 h-8 text-slate-900" />
                    </div>
                    <h2 className="text-2xl font-black text-slate-900 uppercase tracking-tight mb-2">Report In, Commander</h2>
                    <p className="text-slate-500 font-medium text-sm mb-6">
                        You've been active for a while. How is the system performing?
                    </p>

                    <div className="flex justify-center space-x-2 mb-6">
                        {[1, 2, 3, 4, 5].map((star) => (
                            <button 
                                key={star}
                                onClick={() => setRating(star)}
                                className="focus:outline-none transition-transform active:scale-90 hover:scale-110"
                            >
                                <StarIcon className={`w-8 h-8 ${rating >= star ? 'text-yellow-400' : 'text-slate-200'}`} />
                            </button>
                        ))}
                    </div>

                    <div className="mb-6 text-left">
                        <label className="block text-xs font-bold uppercase text-slate-400 mb-2">
                            What feature is missing? (Optional)
                        </label>
                        <textarea 
                            value={feedback}
                            onChange={(e) => setFeedback(e.target.value)}
                            placeholder="e.g. Needs dark mode, better matchmaking..."
                            className="w-full p-3 bg-slate-50 border-2 border-slate-100 rounded-xl text-sm font-medium text-slate-900 focus:border-slate-900 outline-none resize-none h-24"
                        />
                    </div>

                    <button 
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                        className="w-full bg-slate-900 text-white font-black py-4 rounded-xl uppercase tracking-widest hover:bg-slate-800 transition-all shadow-lg active:scale-95 disabled:opacity-50"
                    >
                        {isSubmitting ? 'Transmitting...' : 'Submit Report'}
                    </button>
                </div>
            </div>
        </div>
    );
};

// --- UPDATE MODAL ---
const UpdateModal = ({ onClose }) => {
    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-8 bg-slate-900/90 backdrop-blur-sm animate-fade-in">
            <div className="bg-white w-full max-w-sm md:max-w-md rounded-3xl shadow-2xl overflow-hidden animate-slide-up border-4 border-yellow-400 relative">
                
                <div className="bg-slate-900 p-8 text-center relative overflow-hidden">
                    <div className="absolute top-0 left-0 w-full h-full opacity-10 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-yellow-500 via-transparent to-transparent"></div>
                    <div className="relative z-10">
                        <div className="w-20 h-20 bg-yellow-400 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg shadow-yellow-400/20 animate-bounce-short">
                            <RocketLaunchIcon className="w-10 h-10 text-slate-900" />
                        </div>
                        <h2 className="text-2xl font-black text-white uppercase tracking-tight">Incoming Transmission!</h2>
                        <p className="text-yellow-400 text-xs font-bold uppercase tracking-widest mt-1">Version {APP_VERSION} Deployed</p>
                    </div>
                </div>

                <div className="p-6 text-center">
                    <p className="text-slate-600 font-medium text-sm mb-6 leading-relaxed">
                        We've polished the armor, sharpened the swords, and squashed some bugs in the arena. Update now for the best experience!
                    </p>

                    <div className="space-y-3">
                        <button 
                            onClick={() => alert("Link your App Store URL here!")}
                            className="w-full py-3 bg-slate-900 text-white rounded-xl font-bold uppercase tracking-wider text-xs hover:bg-slate-800 transition-all shadow-lg active:scale-95 flex items-center justify-center"
                        >
                            Update on iOS
                        </button>
                        <button 
                            onClick={() => alert("Link your Play Store URL here!")}
                            className="w-full py-3 bg-white border-2 border-slate-200 text-slate-900 rounded-xl font-bold uppercase tracking-wider text-xs hover:bg-slate-50 hover:border-slate-300 transition-all shadow-sm active:scale-95 flex items-center justify-center"
                        >
                            Update on Android
                        </button>
                    </div>

                    <button 
                        onClick={onClose}
                        className="mt-6 text-xs font-bold text-slate-400 hover:text-slate-600 uppercase tracking-widest transition-colors"
                    >
                        Dismiss (I'll update later)
                    </button>
                </div>
            </div>
        </div>
    );
};

// --- NOTIFICATION TYPE CONFIG ---
const getNotificationConfig = (type) => {
    const configs = {
        reply: { icon: ChatBubbleLeftIcon, label: 'REPLY', bgColor: 'bg-blue-50', iconBg: 'bg-blue-500', textColor: 'text-blue-700' },
        mention: { icon: AtSymbolIcon, label: 'MENTION', bgColor: 'bg-purple-50', iconBg: 'bg-purple-500', textColor: 'text-purple-700' },
        upvote: { icon: HandThumbUpIcon, label: 'UPVOTE', bgColor: 'bg-green-50', iconBg: 'bg-green-500', textColor: 'text-green-700' },
        award: { icon: GiftIcon, label: 'AWARD', bgColor: 'bg-yellow-50', iconBg: 'bg-yellow-500', textColor: 'text-yellow-700' },
        follow: { icon: UserPlusIcon, label: 'FOLLOWER', bgColor: 'bg-pink-50', iconBg: 'bg-pink-500', textColor: 'text-pink-700' },
        following_activity: { icon: MegaphoneIcon, label: 'FOLLOWING', bgColor: 'bg-indigo-50', iconBg: 'bg-indigo-500', textColor: 'text-indigo-700' },
        conquest_alert: { icon: FireIcon, label: 'CONQUEST', bgColor: 'bg-red-50', iconBg: 'bg-red-500', textColor: 'text-red-700' },
        turn_alert: { icon: PlayIcon, label: 'ARENA', bgColor: 'bg-orange-50', iconBg: 'bg-orange-500', textColor: 'text-orange-700' },
        game_alert: { icon: TrophyIcon, label: 'GAME', bgColor: 'bg-orange-50', iconBg: 'bg-orange-500', textColor: 'text-orange-700' },
        urgent_alert: { icon: ExclamationTriangleIcon, label: 'URGENT', bgColor: 'bg-red-50', iconBg: 'bg-red-600', textColor: 'text-red-700' },
        referral_success: { icon: GiftIcon, label: 'REFERRAL', bgColor: 'bg-emerald-50', iconBg: 'bg-emerald-500', textColor: 'text-emerald-700' },
        system: { icon: ServerIcon, label: 'SYSTEM', bgColor: 'bg-slate-100', iconBg: 'bg-slate-600', textColor: 'text-slate-700' },
    };
    return configs[type] || { icon: BellIcon, label: 'ALERT', bgColor: 'bg-slate-50', iconBg: 'bg-slate-500', textColor: 'text-slate-700' };
};

// --- SWIPEABLE NOTIFICATION ITEM ---
const NotificationItem = ({ n, onClick, onDelete }) => {
    const [offset, setOffset] = useState(0);
    const [isSwiping, setIsSwiping] = useState(false);
    const startX = useRef(0);
    const DELETE_THRESHOLD = 100;

    const handleTouchStart = (e) => {
        startX.current = e.touches[0].clientX;
        setIsSwiping(true);
    };

    const handleTouchMove = (e) => {
        if (!isSwiping) return;
        const currentX = e.touches[0].clientX;
        const diff = currentX - startX.current;
        if (diff > 0) {
            setOffset(Math.min(diff, 150));
            if (diff > 10 && e.cancelable) e.preventDefault();
        }
    };

    const handleTouchEnd = () => {
        setIsSwiping(false);
        if (offset > DELETE_THRESHOLD) {
            setOffset(DELETE_THRESHOLD);
        } else {
            setOffset(0);
        }
    };

    const config = getNotificationConfig(n.type);
    const Icon = config.icon;
    const bgColor = n.read ? 'bg-white' : config.bgColor;
    const isUrgent = ['conquest_alert', 'urgent_alert'].includes(n.type);

    return (
        <div className="relative overflow-hidden">
            <div className="absolute inset-0 bg-red-500 flex items-center justify-start pl-6">
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        onDelete(n.id);
                    }}
                    className="flex items-center text-white font-bold text-xs uppercase tracking-wider"
                >
                    <TrashIcon className="w-5 h-5 mr-2" /> Delete
                </button>
            </div>

            <div
                onClick={() => offset === 0 && onClick(n)}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                style={{ transform: `translateX(${offset}px)` }}
                className={`relative p-4 md:p-5 border-b border-slate-100 ${bgColor} hover:bg-slate-100 cursor-pointer transition-transform duration-200 ease-out flex items-start select-none`}
            >
                {!n.read && <div className="absolute top-4 md:top-5 right-4 md:right-5 w-2 h-2 md:w-2.5 md:h-2.5 bg-blue-500 rounded-full animate-pulse"></div>}

                {/* Icon with colored background */}
                <div className={`mt-0.5 mr-3 md:mr-4 flex-shrink-0 w-8 h-8 md:w-10 md:h-10 rounded-full ${config.iconBg} flex items-center justify-center ${isUrgent ? 'animate-pulse' : ''}`}>
                    <Icon className="w-4 h-4 md:w-5 md:h-5 text-white" />
                </div>

                <div className="flex-1 min-w-0">
                    {/* Category Label */}
                    <div className="flex items-center mb-1">
                        <span className={`text-[9px] font-black ${config.textColor} uppercase tracking-widest px-1.5 py-0.5 rounded ${config.bgColor} border border-current/20`}>
                            {config.label}
                        </span>
                    </div>

                    <h4 className={`text-sm md:text-base font-bold ${config.textColor}`}>{n.title}</h4>
                    <p className="text-xs md:text-sm text-slate-600 mt-1 leading-relaxed break-words">{n.message}</p>
                    <span className="text-[10px] font-bold text-slate-400 mt-2 block uppercase tracking-wider">
                        {n.createdAt?.seconds ? formatDistanceToNow(new Date(n.createdAt.seconds * 1000), { addSuffix: true }) : 'Just now'}
                    </span>
                </div>
            </div>
        </div>
    );
};

// --- NOTIFICATION DROPDOWN ---
const NotificationPanel = ({ notifications, currentUser, onClose, onMarkAllRead }) => {
    const navigate = useNavigate();
    const panelRef = useRef(null);
    const [isDeletingAll, setIsDeletingAll] = useState(false);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (panelRef.current && !panelRef.current.contains(event.target)) {
                onClose();
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [onClose]);

    const handleClick = async (notif) => {
        if (!notif.read) {
            await markNotificationRead(notif.uid, notif.id);
        }
        onClose();
        if (notif.link) navigate(notif.link);
    };

    const handleDelete = async (notifId) => {
        if (currentUser) {
            await deleteNotification(currentUser.uid, notifId);
        }
    };

    const handleDeleteAll = async () => {
        if (!currentUser) return;
        if (window.confirm("Clear all notifications?")) {
            setIsDeletingAll(true);
            await deleteAllNotifications(currentUser.uid);
            setIsDeletingAll(false);
        }
    };

    return (
        <div ref={panelRef} className="fixed top-16 md:top-20 right-4 md:right-8 w-80 md:w-[28rem] lg:w-96 bg-white rounded-2xl shadow-2xl border border-slate-200 z-[60] animate-fade-in overflow-hidden flex flex-col" style={{ maxHeight: 'calc(100vh - 5rem)' }}>
            <div className="p-4 border-b border-slate-100 flex justify-between items-center bg-slate-900 text-white flex-shrink-0">
                <div className="flex items-center">
                    <BellIcon className="w-5 h-5 mr-2 text-yellow-500" />
                    <span className="font-black uppercase tracking-wider text-sm">War Room Comms</span>
                </div>
                <button onClick={onClose} className="text-slate-400 hover:text-white">
                    <XMarkIcon className="w-5 h-5" />
                </button>
            </div>

            <div className="bg-slate-50 px-4 py-2 border-b border-slate-200 flex justify-between items-center">
                <button onClick={onMarkAllRead} className="flex items-center text-[10px] font-bold uppercase tracking-wider text-blue-600 hover:text-blue-800 transition-colors">
                    <CheckIcon className="w-3 h-3 mr-1" /> Mark All Read
                </button>
                <button onClick={handleDeleteAll} disabled={isDeletingAll || notifications.length === 0} className="flex items-center text-[10px] font-bold uppercase tracking-wider text-red-500 hover:text-red-700 transition-colors disabled:opacity-50">
                    {isDeletingAll ? 'Clearing...' : <><TrashIcon className="w-3 h-3 mr-1" /> Delete All</>}
                </button>
            </div>

            <div className="overflow-y-auto bg-white flex-1">
                {notifications.length === 0 ? (
                    <div className="p-8 text-center text-slate-400 text-sm font-medium">
                        No new signals, Commander.
                    </div>
                ) : (
                    notifications.map(n => (
                        <NotificationItem 
                            key={n.id} 
                            n={n} 
                            onClick={handleClick} 
                            onDelete={handleDelete} 
                        />
                    ))
                )}
            </div>
            
            <div className="p-2 bg-slate-50 text-center text-[9px] text-slate-400 font-bold uppercase tracking-widest border-t border-slate-100">
                Swipe Right to Delete
            </div>
        </div>
    );
};

function Layout() {
  const { currentUser, loginWithEmail, signupWithEmail, isPro } = useAuth(); 
  const { forceRefresh } = useData(); 
  const location = useLocation();

  const [isSignup, setIsSignup] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [handle, setHandle] = useState('');
  const [referralCode, setReferralCode] = useState(''); 
  const [showPassword, setShowPassword] = useState(false);
  const [authError, setAuthError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [showModerationPanel, setShowModerationPanel] = useState(false); 
  
  // Onboarding, Update & Review State
  const [showTour, setShowTour] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false); 
  const [showLegalModal, setShowLegalModal] = useState(false); // NEW: Legal Modal State
  const tourTriggeredRef = useRef(false);

  // Notification State
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);

  // --- DYNAMIC LINK LOGIC (For Login Screen) ---
  const isNative = Capacitor.isNativePlatform();
  const isIOS = Capacitor.getPlatform() === 'ios';
  const baseUrl = isNative ? "https://verbi-bd770.web.app" : window.location.origin;

  const termsLink = isIOS 
      ? "https://www.apple.com/legal/internet-services/itunes/dev/stdeula/" 
      : `${baseUrl}/terms.html`;
  
  const privacyLink = `${baseUrl}/privacy_policy.html`;

  // --- REVIEW MODAL TIMER (5 MINUTES) ---
  useEffect(() => {
      const hasReviewed = localStorage.getItem('verbi_review_completed');
      if (currentUser && !hasReviewed) {
          const timer = setTimeout(() => {
              setShowReviewModal(true);
          }, 300000); 
          return () => clearTimeout(timer);
      }
  }, [currentUser]);

  // --- PUSH NOTIFICATIONS ---
  useEffect(() => {
    if (currentUser && Capacitor.isNativePlatform()) {
      const notificationsEnabled = currentUser.preferences?.notificationsEnabled !== false; 

      const setupPush = async () => {
        if (notificationsEnabled) {
            try {
              let permStatus = await PushNotifications.checkPermissions();
              if (permStatus.receive === 'prompt') {
                permStatus = await PushNotifications.requestPermissions();
              }
              if (permStatus.receive === 'granted') {
                await PushNotifications.register();
              }
            } catch (e) {
              console.error('Push registration error:', e);
            }
        } else {
            try {
                await PushNotifications.removeAllListeners();
                await PushNotifications.unregister();
            } catch (e) {
                console.warn('Could not unregister push:', e);
            }
        }
      };

      setupPush();

      if (notificationsEnabled) {
          const registrationListener = PushNotifications.addListener('registration', (token) => {
            saveFcmToken(currentUser.uid, token.value);
          });

          const errorListener = PushNotifications.addListener('registrationError', () => {
            // Push registration error - silent
          });

          const pushListener = PushNotifications.addListener('pushNotificationReceived', () => {
            // Push received
          });

          return () => {
            registrationListener.remove();
            errorListener.remove();
            pushListener.remove();
          };
      }
    }
  }, [currentUser?.uid, currentUser?.preferences?.notificationsEnabled]); 

  // LISTEN FOR FIRESTORE NOTIFICATIONS
  useEffect(() => {
      if (!currentUser) return;
      
      const q = query(
          collection(db, 'notifications', currentUser.uid, 'userNotifications'),
          orderBy('createdAt', 'desc'),
          limit(20)
      );

      const unsubscribe = onSnapshot(q, (snapshot) => {
          const notifs = snapshot.docs.map(doc => ({ id: doc.id, uid: currentUser.uid, ...doc.data() }));
          setNotifications(notifs);
          setUnreadCount(notifs.filter(n => !n.read).length);
      });

      return () => unsubscribe();
  }, [currentUser]);

  // --- CHECK FOR UPDATES & TOUR (UID NAMESPACED) ---
  useEffect(() => {
    if (currentUser && !tourTriggeredRef.current) {
        const tourKey = `verbi_tour_completed_${currentUser.uid}`;
        const hasSeenTour = localStorage.getItem(tourKey);
        const lastSeenVersion = localStorage.getItem('verbi_app_version');

        if (!hasSeenTour) {
            tourTriggeredRef.current = true;
            localStorage.setItem('verbi_app_version', APP_VERSION);
            setTimeout(() => setShowTour(true), 1000);
        } 
        else if (lastSeenVersion !== APP_VERSION) {
            tourTriggeredRef.current = true;
            setTimeout(() => setShowUpdateModal(true), 2000);
        }
    }
  }, [currentUser]);

  const handleTourComplete = () => {
      if (currentUser) {
          localStorage.setItem(`verbi_tour_completed_${currentUser.uid}`, 'true');
      }
      setShowTour(false);
  };

  const handleUpdateDismiss = () => {
      localStorage.setItem('verbi_app_version', APP_VERSION);
      setShowUpdateModal(false);
  };

  const handleMarkAllRead = async () => {
      if (currentUser) {
          await markAllNotificationsRead(currentUser.uid);
      }
  };

  // --- MODIFIED AUTH HANDLER ---
  const handleAuthSubmit = async (e) => {
    e.preventDefault();
    setAuthError(null);
    
    // If it's signup, STOP here and show legal modal first
    if (isSignup) {
        if (!handle.trim()) { setAuthError("Username is required."); return; }
        if (/\s/.test(handle)) { setAuthError("Username cannot contain spaces."); return; }
        setShowLegalModal(true);
    } else {
        // Normal Login
        setIsSubmitting(true);
        try {
            await loginWithEmail(email, password);
        } catch (err) {
            setAuthError(err.message.replace('Firebase: ', ''));
        }
        setIsSubmitting(false);
    }
  };

  // --- NEW: ACTUAL SIGNUP FUNCTION (Triggered by Modal) ---
  const handleFinalizeSignup = async () => {
      setIsSubmitting(true);
      try {
          await signupWithEmail(email, password, handle, referralCode);
          setShowLegalModal(false); // Close on success
      } catch (err) {
          setAuthError(err.message.replace('Firebase: ', ''));
          setShowLegalModal(false); // Close to show error on main form
      }
      setIsSubmitting(false);
  };

  if (!currentUser) {
      return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center p-6 md:p-10">

        {/* NEW: LEGAL MODAL */}
        <LegalModal
            isOpen={showLegalModal}
            onClose={() => setShowLegalModal(false)}
            onConfirm={handleFinalizeSignup}
            isSubmitting={isSubmitting}
        />

        <div className="w-full max-w-md md:max-w-lg">
          <div className="text-center mb-8 md:mb-10">
            <div className="w-16 h-16 md:w-20 md:h-20 bg-slate-900 rounded-2xl mx-auto mb-6 flex items-center justify-center shadow-xl shadow-slate-200">
              <span className="text-white font-black text-3xl md:text-4xl">V</span>
            </div>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-black text-slate-900 tracking-tight mb-2">
              {isSignup ? 'Create Account' : 'Welcome Back'}
            </h1>
            <p className="text-sm md:text-base text-slate-500 font-medium">
              {isSignup ? 'Join the arena of intelligent debate.' : 'Sign in to continue your win streak.'}
            </p>
          </div>

          <form onSubmit={handleAuthSubmit} className="space-y-4 md:space-y-5 mb-8">
            
            {/* UPDATED ERROR DISPLAY WITH STYLING AND ICON */}
            {authError && (
              <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 rounded-r-xl flex items-start animate-fade-in shadow-sm">
                <ExclamationTriangleIcon className="w-5 h-5 text-red-500 mr-3 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="text-xs font-black text-red-700 uppercase tracking-wider mb-1">Authentication Error</h4>
                  <p className="text-sm font-medium text-red-600 leading-tight">
                    {getFriendlyErrorMessage(authError)}
                  </p>
                </div>
              </div>
            )}

            {isSignup && (
              <>
                  <div>
                    <input
                      type="text"
                      placeholder="Username (Handle)"
                      value={handle}
                      onChange={(e) => setHandle(e.target.value)}
                      className="w-full p-4 bg-slate-50 border-2 border-slate-100 rounded-xl focus:outline-none focus:border-slate-900 transition-colors text-slate-900 font-medium"
                      required
                    />
                    <p className="text-[10px] text-slate-400 mt-1 ml-1">This will be your unique referral code.</p>
                  </div>
                  <div>
                    <div className="relative">
                        <GiftIcon className="absolute top-4 left-4 w-5 h-5 text-purple-400" />
                        <input
                          type="text"
                          placeholder="Referral Code (Optional)"
                          value={referralCode}
                          onChange={(e) => setReferralCode(e.target.value)}
                          className="w-full p-4 pl-12 bg-white border-2 border-purple-100 rounded-xl focus:outline-none focus:border-purple-500 transition-colors text-slate-900 font-bold placeholder:font-medium placeholder:text-slate-400"
                        />
                    </div>
                    <p className="text-[10px] text-purple-500 mt-1 ml-1 font-bold">Enter a friend's handle to unlock a Reward Crate.</p>
                  </div>
              </>
            )}
            <div>
              <input
                type="email"
                placeholder="Email Address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full p-4 md:p-5 text-sm md:text-base bg-slate-50 border-2 border-slate-100 rounded-xl focus:outline-none focus:border-slate-900 transition-colors text-slate-900 font-medium"
                required
              />
            </div>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full p-4 md:p-5 text-sm md:text-base bg-slate-50 border-2 border-slate-100 rounded-xl focus:outline-none focus:border-slate-900 transition-colors text-slate-900 font-medium pr-12 md:pr-14"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 md:right-5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 p-1"
              >
                {showPassword ? <EyeSlashIcon className="w-5 h-5 md:w-6 md:h-6" /> : <EyeIcon className="w-5 h-5 md:w-6 md:h-6" />}
              </button>
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-slate-900 text-white font-bold py-4 md:py-5 px-8 text-sm md:text-base rounded-xl hover:bg-slate-800 transition-all active:scale-95 flex items-center justify-center"
            >
              {isSubmitting ? 'Processing...' : (isSignup ? 'Create Account' : 'Sign In')}
            </button>
          </form>

          <div className="relative mb-8">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-100"></div></div>
          </div>

          <p className="text-center mt-8 text-slate-600 font-medium">
            {isSignup ? 'Already have an account?' : "Don't have an account?"}
            <button 
              onClick={() => {
                setIsSignup(!isSignup);
                setAuthError(null); 
              }}
              className="ml-2 text-slate-900 font-black hover:underline"
            >
              {isSignup ? 'Sign In' : 'Sign Up'}
            </button>
          </p>

          {/* NEW: VISIBLE TERMS & PRIVACY FOOTER (COMPLIANCE) */}
          <div className="mt-8 pt-6 border-t border-slate-50">
             <div className="flex justify-center space-x-6 text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                <a href={termsLink} target="_blank" rel="noopener noreferrer" className="hover:text-slate-600 transition-colors">Terms of Service</a>
                <a href={privacyLink} target="_blank" rel="noopener noreferrer" className="hover:text-slate-600 transition-colors">Privacy Policy</a>
             </div>
             <p className="text-center text-[10px] text-slate-300 mt-2 font-medium">
                Verbi &copy; {new Date().getFullYear()}
             </p>
          </div>

        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      
      {/* Modals */}
      <OnboardingTour isActive={showTour} onComplete={handleTourComplete} />
      {showUpdateModal && <UpdateModal onClose={handleUpdateDismiss} />}
      {showReviewModal && <ReviewModal onClose={() => setShowReviewModal(false)} />}

      {/* Mobile Top Bar */}
      <div
        className="fixed top-0 left-0 right-0 bg-white/90 backdrop-blur-md border-b border-slate-100 z-40 flex items-center justify-between px-4 md:px-6"
        style={{
            paddingTop: 'env(safe-area-inset-top)',
            height: 'calc(4rem + env(safe-area-inset-top))'
        }}
      >
          {/* Left: Menu Trigger & Logo */}
          <div className="flex items-center laptop:hidden">
              <button onClick={() => setIsMenuOpen(true)} className="p-2 md:p-2.5 text-slate-900 hover:bg-slate-100 rounded-lg transition-colors mr-1 md:mr-2">
                  <Bars3Icon className="w-6 h-6 md:w-7 md:h-7" />
              </button>
              <Link to="/" className="flex items-center">
                <div className={`w-8 h-8 md:w-10 md:h-10 rounded-lg flex items-center justify-center shadow-sm ${isPro ? 'bg-gradient-to-br from-yellow-400 to-yellow-600' : 'bg-slate-900'}`}>
                   <span className={`${isPro ? 'text-slate-900' : 'text-white'} font-black text-xl md:text-2xl`}>V</span>
                </div>
              </Link>
          </div>

          {/* Right: Notifications & Status */}
          <div className="flex items-center space-x-3 md:space-x-4">
              <button
                onClick={() => setShowNotifications(!showNotifications)}
                className="relative p-2 md:p-2.5 text-slate-500 hover:bg-slate-100 rounded-lg transition-colors"
              >
                  <BellIcon className={`w-6 h-6 md:w-7 md:h-7 ${unreadCount > 0 ? 'text-slate-900' : 'text-slate-400'}`} />
                  {unreadCount > 0 && (
                      <span className="absolute top-1.5 right-1.5 w-2.5 h-2.5 md:w-3 md:h-3 bg-red-500 border-2 border-white rounded-full"></span>
                  )}
              </button>

              {/* Pro Badge (Visible on Mobile & Tablet) */}
              <div className="laptop:hidden">
                  <span className={`text-[10px] md:text-xs font-black uppercase tracking-widest px-2 md:px-3 py-1 md:py-1.5 rounded-md border ${isPro ? 'bg-yellow-50 text-yellow-700 border-yellow-200' : 'bg-slate-50 text-slate-400 border-slate-200'}`}>
                      {isPro ? 'PRO' : 'FREE'}
                  </span>
              </div>
          </div>
      </div>

      {/* OFFLINE CONNECTIVITY INDICATOR */}
      <OfflineIndicator />

      {/* NOTIFICATION DROPDOWN (Fixed Positioned) */}
      {showNotifications && (
          <NotificationPanel
              notifications={notifications}
              currentUser={currentUser}
              onClose={() => setShowNotifications(false)}
              onMarkAllRead={handleMarkAllRead}
          />
      )}

      <SideNavBar
        isOpen={isMenuOpen}
        onClose={() => setIsMenuOpen(false)}
        onOpenAdmin={() => setShowAdminPanel(true)}
        onOpenModeration={() => setShowModerationPanel(true)}
      />

      <main
        className="laptop:pt-0 laptop:pl-72 min-h-screen"
        style={{ paddingTop: 'calc(4rem + env(safe-area-inset-top))' }}
      >
        {/* PASS MENU CONTEXT HERE - Wrapped in ErrorBoundary */}
        <ErrorBoundary>
          <Outlet context={{ setIsMenuOpen }} />
        </ErrorBoundary>
      </main>

      <AdminDashboard
        isOpen={showAdminPanel}
        onClose={() => setShowAdminPanel(false)}
        onRefreshComplete={forceRefresh}
      />

      <ModerationPanel
        isOpen={showModerationPanel}
        onClose={() => setShowModerationPanel(false)}
      />
    </div>
  );
}

export default Layout;